import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { COLUMN_DEFINITIONS, COLUMNS_PREFERENCES_KEY } from '../utils/employeeConstants';

const useEmployeeColumns = () => {
  const { t } = useTranslation();

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem(COLUMNS_PREFERENCES_KEY);
    if (saved) {
      try { return JSON.parse(saved); } catch { /* ignore */ }
    }
    return COLUMN_DEFINITIONS
      .filter(col => col.fixed || col.defaultVisible)
      .map(col => col.id);
  });

  const toggleColumnVisibility = (columnId) => {
    setVisibleColumns(prev => {
      const next = prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId];
      localStorage.setItem(COLUMNS_PREFERENCES_KEY, JSON.stringify(next));
      return next;
    });
  };

  const getVisibleColumnDefs = useCallback(() => {
    return COLUMN_DEFINITIONS.filter(col => visibleColumns.includes(col.id));
  }, [visibleColumns]);

  const getColumnLabel = useCallback((columnId) => {
    const column = COLUMN_DEFINITIONS.find((col) => col.id === columnId);
    if (column?.labelKey) return t(column.labelKey);
    const keyMap = {
      select: 'employeesPage.columns.select',
      registration_number: 'employeesPage.columns.registrationNumber',
      last_name: 'employeesPage.columns.lastName',
      first_name: 'employeesPage.columns.firstName',
      email: 'employeesPage.columns.email',
      phone_number: 'employeesPage.columns.phone',
      birth_date: 'employeesPage.columns.birthDate',
      job_name: 'employeesPage.columns.jobName',
      department_name: 'employeesPage.columns.departmentName',
      site_name: 'employeesPage.columns.siteName',
      status: 'employeesPage.columns.status',
      contract_type: 'employeesPage.columns.contractType',
      role: 'employeesPage.columns.role',
      badge_number: 'employeesPage.columns.badgeNumber',
      fingerprint_id: 'employeesPage.columns.fingerprintId',
      face_id: 'employeesPage.columns.faceId',
      card_id: 'employeesPage.columns.cardId',
      supervisor_name: 'employeesPage.columns.supervisorName',
      coach_name: 'employeesPage.columns.coachName',
      mentor_name: 'employeesPage.columns.mentorName',
      hire_date: 'employeesPage.columns.hireDate',
      contract_end_date: 'employeesPage.columns.contractEndDate',
      created_at: 'employeesPage.columns.createdAt',
      updated_at: 'employeesPage.columns.updatedAt',
      is_postier: 'employeesPage.columns.isPostier',
      regime: 'employeesPage.columns.regime',
      actions: 'employeesPage.columns.actions',
    };
    return keyMap[columnId] ? t(keyMap[columnId]) : columnId;
  }, [t]);

  return { visibleColumns, toggleColumnVisibility, getVisibleColumnDefs, getColumnLabel };
};

export default useEmployeeColumns;
